import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  SalaryAdvanceRequestFragment,
  SalaryAdvanceRequestsCollectionDocument,
  SalaryAdvanceRequestsCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMemo } from 'modules/core/hooks/useMemo'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { getStatusLabel, getStatusVariant } from 'modules/salary_advance/lib/requestHelpers'

export const RequestsTable = () => {
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'

  const dataSourceOptions = useMemo(() => {
    return {
      document: SalaryAdvanceRequestsCollectionDocument,
      fetchPolicy: 'cache-and-network' as const,
      mapData: (data: SalaryAdvanceRequestsCollectionQuery) => {
        return data.salaryAdvance.requestsConnection.edges.map((edge) => edge.node)
      },
      mapPagination: (data: SalaryAdvanceRequestsCollectionQuery) => {
        const count = data.salaryAdvance.requestsConnection.totalCount ?? 0
        return { total: count }
      },
      mapVariables: () => ({}),
    }
  }, [])

  const source = useGraphqlDataCollectionSource(dataSourceOptions)

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('common.employee'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  if (!item.employeeName) {
                    return { type: 'text', value: '-' }
                  }

                  return {
                    type: 'person',
                    value: {
                      firstName: item.employeeName.firstName,
                      lastName: item.employeeName.lastName,
                      src: buildUrl(item.employeeName.avatar?.url ?? ''),
                    },
                  }
                },
              },
              {
                label: i18n.t('common.date.date'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'text',
                    value: i18nDateToString(new Date(item.createdAt), 'ui'),
                  }
                },
              },
              {
                label: i18n.t('salary_advance.request.table.columns.requested_amount'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'text',
                    value: MoneyWithCurrency({
                      amount: item.amountCents,
                      currency: item.currency,
                      locale,
                    }),
                  }
                },
              },
              {
                label: i18n.t('common.status'),
                render: (item: SalaryAdvanceRequestFragment) => {
                  return {
                    type: 'status',
                    value: {
                      label: getStatusLabel(item.status),
                      status: getStatusVariant(item.status),
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}
